import React from "react";
import { motion } from "framer-motion";
import { Wand2, PackageOpen, QrCode, Layers, BadgeCheck } from "lucide-react";

const FEATURES = [
  {
    icon: Wand2,
    title: "AI Room Visualization",
    body: "Replace products instantly in any customer photo.",
  },
  {
    icon: PackageOpen,
    title: "Product Catalog",
    body: "Upload thousands of products and keep them organized.",
  },
  {
    icon: QrCode,
    title: "QR Experience",
    body: "Customers scan a QR code and start visualizing right away.",
  },
  {
    icon: Layers,
    title: "Multi-product Replacement",
    body: "Furniture, paint, tiles, wallpaper, lighting, curtains, flooring — across every room.",
    // tags: ["Furniture", "Paint", "Tiles", "Wallpaper", "Lighting", "Curtains", "Flooring", "Kitchen", "Bathroom", "Bedroom", "Living Room"],
  },
  {
    icon: BadgeCheck,
    title: "Brand Consistency",
    body: "Every render uses your own products — nothing generic.",
  },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const cardIn = {
  hidden: { opacity: 0, y: 28 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Features() {
  return (
    <section id="features" className="bg-white py-16 sm:py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="text-sm font-bold uppercase tracking-widest text-primary">Features</p>
          <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
            Everything you need to sell visually
          </h2>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                variants={cardIn}
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 22 }}
                className={`group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm shadow-neutral-900/[0.03] transition-shadow duration-300 hover:border-transparent hover:shadow-xl hover:shadow-neutral-900/10 sm:p-8 ${
                  feature.tags ? "sm:col-span-2 lg:col-span-1" : ""
                }`}
              >
                {/* Animated gradient border on hover */}
                <div
                  className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    padding: 1,
                    background:
                      "linear-gradient(135deg, hsl(var(--primary)/0.5), transparent 40%, transparent 60%, hsl(var(--primary)/0.5))",
                    WebkitMask:
                      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                  }}
                  aria-hidden="true"
                />

                {/* Corner glow */}
                <div
                  className="pointer-events-none absolute -right-14 -top-14 h-48 w-48 rounded-full bg-primary/0 blur-3xl transition-all duration-500 group-hover:bg-primary/30"
                  aria-hidden="true"
                />
                <div
                  className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-primary/0 blur-3xl transition-all duration-500 group-hover:bg-primary/15"
                  aria-hidden="true"
                />

                <div className="relative">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:scale-110 group-hover:rotate-3">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-neutral-900">{feature.title}</h3>
                  <p className="mt-2 leading-relaxed text-neutral-500">{feature.body}</p>
                  {feature.tags && (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {feature.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-600 transition-colors duration-300 group-hover:bg-primary/10 group-hover:text-primary"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
